import  { useEffect, useState } from "react";
import {  useNavigate, useLocation } from "react-router-dom";
import { ArrowLeftIcon, CalendarIcon, ClockIcon } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription, DialogFooter, DialogHeader } from "../../components/ui/dialog";
import { Header } from "../../components/Header/Header";
import { ComponentHeader } from "../../components/Header/ComponentHeader";
import { predictionsApi, Product, ApiResponse } from "../../api/predictionsApi";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Prediction } from "../../types/prediction";
import { Loader } from "../../components/ui/loader";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import tick from "../../assets/tick.png";
import weed from "../../assets/weed.png";
import expected from "../../assets/expected.png";
import { COUNTRY_DATA } from '../../utils/countryCode';
import React from "react";

// Add ReadOnlyInput component
const ReadOnlyInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  (props, ref) => (
    <input
      {...props}
      ref={ref}
      readOnly
    />
  )
);

export const MyRecommendation = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const countryMatch = location.pathname.match(/^\/([a-z]{2,})\//i);
  const country = countryMatch ? countryMatch[1] : "in";
  const { t } = useTranslation();
  const [isHowToUseOpen, setIsHowToUseOpen] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [predictionDetails, setPredictionDetails] = useState<Prediction | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [productDetails, setProductDetails] = useState<Product | null>(null);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const timeZone = COUNTRY_DATA[country]?.timeZone || 'UTC';


  useEffect(() => {
    if (!location.state?.id) {
      navigate(`/${country}/my-predictions`);
      return;
    }
    fetchPredictionDetails();
  }, [location.state?.id]);

  const fetchPredictionDetails = async () => {
    try {
      setIsLoading(true);
      const response = await predictionsApi.getPredictionDetails(location.state.id);
      if (response && response.data) {
        setPredictionDetails(response.data);
      } else {
        toast.error(t('invalidResponseFormat'));
        navigate(`/${country}/my-predictions`);
      }
    } catch (error: any) {
      console.error('Error fetching prediction details:', error);
      toast.error(error.response?.data?.message || t('failedToFetchPredictionDetails'));
      navigate(`/${country}/my-predictions`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductDetails = async () => {
    if (!predictionDetails?.productId) return;
    
    setIsLoadingProduct(true);
    try {
      const response = await predictionsApi.getProductDetails(predictionDetails.productId);
      if (response && response.data) {
        setProductDetails(response.data);
        setIsHowToUseOpen(true);
      } else {
        toast.error(t('invalidResponseFormat'));
      }
    } catch (error: any) {
      console.error('Error fetching product details:', error);
      toast.error(error.response?.data?.message || t('failedToFetchProductDetails'));
    } finally {
      setIsLoadingProduct(false);
    }
  };

  const handleGoBack = () => {
    const state = location.state as { returnPage?: number };
    navigate(`/${country}/my-predictions`, { 
      state: { 
        returnPage: state?.returnPage || 1 
      } 
    });
  };

  const handleConfirmApplication = async () => {
    if (!location.state?.id || !selectedDate) return;

    setIsConfirming(true);
    try {
      await predictionsApi.confirmPrediction(location.state.id, format(selectedDate, 'yyyy-MM-dd'));
      toast.success(t('applicationConfirmed'));
      // navigate('/my-predictions');
      fetchPredictionDetails();
      setIsConfirming(false);
      setIsConfirmDialogOpen(false);
      

    } catch (error: any) {
      console.error('Error confirming application:', error);
      toast.error(error.response?.data?.message || t('errorConfirmingApplication'));
    } finally {
      setIsConfirming(false);
      setIsConfirmDialogOpen(false);
    }
  };

  // Calculate min and max dates based on transplant date
  const getMinMaxDates = () => {
    if (!predictionDetails?.attributes?.transplant_date) {
      const today = new Date();
      return { minDate: today, maxDate: today };
    }
    
    const transplantDate = new Date(predictionDetails.attributes.transplant_date);
    const today = new Date();
    // Ensure minDate is not after today
    const minDate = transplantDate > today ? today : transplantDate;
    return {
      minDate,
      maxDate: today
    };
  };

  const { minDate, maxDate } = getMinMaxDates();

  // Check if transplant date is less than or equal to current date
  const isLandPrepDateValid = () => {
    if (!predictionDetails?.attributes?.transplant_date) return false;
    const transplantDate = new Date(predictionDetails.attributes.transplant_date);
    const currentDate = new Date();
    return transplantDate <= currentDate;
  };

  // Check if prediction is already applied
  const isPredictionApplied = () => {
    return predictionDetails?.status === "APPLIED" || predictionDetails?.status === "NOT_APPLICABLE";
  };

  const handleOpenExternalLink = (url: string) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center" data-testid="loading-indicator">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <Loader isLoading={true} message={t('loading')} />
        </div>
      </div>
    );
  }

  if (!predictionDetails) {
    return <div>{t('noPredictionDetails')}</div>;
  }

  return (
    <div>
      <Header className="hidden sm:flex" />
      <ComponentHeader name={t('myRecommendation')} handleClick={handleGoBack} />
      <div className="bg-[#fcfcfc] flex flex-row justify-center w-full">
        <div className="bg-[#fcfcfc] w-full relative">
          <nav className="fixed left-0 top-[60px] hidden sm:flex flex-col w-[100px] h-[calc(100vh-60px)] items-start gap-5 p-2.5 bg-primary-new500">
            <div 
              className="cursor-pointer relative w-20 h-20 bg-[#014224] rounded-[10px] flex items-center justify-center" 
              onClick={handleGoBack}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleGoBack();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={t('back')}
            >
              <img
                className="w-11 h-11"
                alt={t('predictions')}
                src="https://c.animaapp.com/m9qnee32rCPMMo/img/icon-prediction.svg"
              />
            </div>
          </nav>

          <main>
            <div className="sm:ml-[100px] sm:pt-5 sm:w-[calc(100%-100px)] h-[calc(100dvh-60px)] overflow-auto">
              <div className="hidden sm:flex items-center gap-3.5 px-6">
                <Button
                  variant="ghost"
                  size="icon"
                  className="p-0"
                  onClick={handleGoBack}
                  aria-label={t('back')}
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                </Button>
                <h1 className="font-bold text-[#14151c] text-lg tracking-[-0.54px] leading-[22px]">
                  {t('myRecommendation')}
                </h1>
              </div>

              <Card className="mx-4 sm:mx-6 mt-4 rounded border border-[#cdcdcd] overflow-hidden">
                <CardContent className="p-0 bg-white">
                  <div className="flex items-center flex-wrap md:flex-nowrap md:gap-[8px] w-full bg-lite-bg-grey">
                    <div className="flex flex-col min-w-[50%] md:min-w-[25%] items-start gap-1 p-2.5 flex-1">
                      <div className="w-[160px] h-[80px] mx-4">
<img
className="w-full h-full object-contain"
alt={predictionDetails?.product?.name}
src={predictionDetails?.product?.logo}
/>
</div>
                    </div>
                  </div>
                  <div className="flex  sm:px-4 mx-6 sm:bg-neutral-50 mb-6 flex-wrap">
                    <div className="flex flex-col min-w-[50%] min-w-[50%] sm:min-w-[25%] items-start gap-1 p-2.5 flex-1">
                      <div className="text-sm text-black">{t('createdOn')}</div>
                      <div data-testid="prediction-name" className="font-bold text-black text-base">
                        {predictionDetails?.createdAt ?
                          format(new Date(predictionDetails.createdAt), 'd MMM yyyy')
                          : "--"}
                      </div>
                    </div>

                    <div className="flex flex-col min-w-[50%] min-w-[50%] sm:min-w-[25%] items-start gap-1 p-2.5 flex-1">
                      <div className="text-sm text-black">{t('landPreparedOn')}</div>
                      <div data-testid="prediction-land-prep" className="font-bold text-black text-base">
                        {predictionDetails?.attributes?.land_preparation_date ?
                          format(new Date(predictionDetails.attributes.land_preparation_date), 'd MMM yyyy')
                          : "--"}
                      </div>
                    </div>

                    <div className="flex flex-col min-w-[50%] min-w-[50%] sm:min-w-[25%] items-start gap-1 p-2.5 flex-1">
                      <div className="text-sm text-black">{t('transplantDate')}</div>
                      <div data-testid="prediction-transplant" className="font-bold text-black text-base">
                        {predictionDetails?.attributes?.transplant_date ?
                          format(new Date(predictionDetails.attributes.transplant_date), 'd MMM yyyy')
                          : "--"}
                      </div>
                    </div>
                    <div className="rounded-sm sm:rounded-none bg-[#3D8156] flex sm:flex-col min-w-[100%] sm:min-w-[25%] items-start gap-[8px] sm:gap-0 p-2.5 flex-1">
                     <div className="flex items-center gap-[8px]">
                     <CalendarIcon className="text-white" />
                      <div className="flex sm:flex-col">
                        <div className="text-sm text-white mr-1">{t('recommendedApplicationDate')}</div>
                        <div data-testid="prediction-status" className="text-white text-sm">
                        {predictionDetails?.recommendations?.recommendation_date ?
                          ` ${format(new Date(predictionDetails.recommendations.recommendation_date), 'd MMM yyyy')}`
                          : "--"}
                        </div>
                      </div>
                     </div>
                    </div>
                    {predictionDetails?.status === "APPLIED" &&
                    <div className="flex flex-col min-w-[50%] min-w-[50%] sm:min-w-[25%] items-start gap-1 p-2.5 flex-1">
                      <div className="text-sm text-black">{t('confirmationDate')}</div>
                      <div data-testid="prediction-land-prep" className="font-bold text-black text-base">
                        {predictionDetails?.applicationDate
                          ? new Date(predictionDetails.applicationDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone })
                          : "--"}
                      </div>
                    </div>
                    }

                    <div className="w-full bg-[#F5F6FA] rounded-lg flex flex-col gap-3 px-4 py-3 mt-4">
                     {predictionDetails?.status !== "NOT_APPLICABLE"  ? 
                     
                       <>
                      
                      <div className="flex items-center gap-3">
                        <img
                          className="w-6 h-6"
                          alt="Weather icon"
                          src={tick}
                        />
                        <span className="text-black text-sm sm:text-base leading-5 sm:leading-6 font-medium">
                          
                          {predictionDetails?.recommendations?.recommendation_date && predictionDetails?.attributes?.transplant_date
    ? `${Math.ceil(
        (new Date(predictionDetails.recommendations.recommendation_date).getTime() -
          new Date(predictionDetails.attributes.transplant_date).getTime()) /
        (1000 * 60 * 60 * 24)
      )} days after Transplanting / Sowing`
    : "--"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <img
                          className="w-6 h-6"
                          alt="Weather icon"
                          src={weed}
                        />
                        <span className="text-black text-sm sm:text-base leading-5 sm:leading-6 font-medium">
                        {predictionDetails.leafDays !== null && predictionDetails.leafDays !== undefined && predictionDetails.leafDays !== ""
                          ? `Weeds will be around ${predictionDetails.leafDays} leaf stage`
                          : "--"}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <img
                          className="w-6 h-6"
                          alt="Weather icon"
                          src={expected}
                        />
                        <span className="text-black text-sm sm:text-base leading-5 sm:leading-6 font-medium">
                        {predictionDetails.recommendedDateTemperature !== null &&
                          predictionDetails.recommendedDateTemperature !== undefined &&
                          predictionDetails.recommendedDateTemperature !== ""
                            ? `Expected temperature on application day : ${predictionDetails.recommendedDateTemperature}°C`
                            : "--"}

                        </span>
                      </div> 
                      </> :
                      
                      <div className="flex items-center">
                      {t('temperatureConditionsNotFavorable')}
                        </div>
}
                      
                  
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end gap-4 p-4">
                    <Button
                      variant="outline"
                      className="h-11 px-3 py-2.5 rounded-[100px] border-primary-new500 text-primary-new500"
                      onClick={() => handleOpenExternalLink('https://www.weedmanagement.in/weed-identification-rifit-plus-rice/')}
                    >
                      {t('identifyYourWeed')}
                    </Button>
                    <Dialog open={isHowToUseOpen} onOpenChange={setIsHowToUseOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="h-11 px-3 py-2.5 rounded-[100px] border-primary-new500 text-primary-new500"
                          onClick={fetchProductDetails}
                          disabled={isLoadingProduct}
                        >
                          {isLoadingProduct ? t('loading') : `${t('viewHowToUse')} ${predictionDetails?.product?.name}`}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{`${t('howToUse')} ${productDetails?.name ?? ''}`}</DialogTitle>
                        </DialogHeader>
                        <hr className=" border: 1px solid #000000" />
                        {productDetails ? (
                          
                          <div>
                            <div className="mb-[13px]">

                            <div className="w-[100px] h-auto">
<img
className="w-full h-full object-contain"
alt={productDetails?.name}
src={productDetails?.logo}
/>
</div>
                              </div>
                            
                            <p className="text-gray-700 pb-5">{productDetails.description}</p>
                            
                            <a 
                                                                href={productDetails.infoUrl}
                                                                target="_blank" 
                                                                rel="noopener noreferrer"
                                                                className="font-regular-caption-1 text-[#0071cd] underline" 
                                                                role="link"
                                                            >
                                                                Know More
                                                            </a>

                          </div>
                        ) : (
                          <div className="flex justify-center items-center py-8">
                            <Loader isLoading={true} message={t('loading')} />
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>

                    <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          className="h-11 px-3 py-2.5 rounded-[100px] bg-primary-new500 text-white"
                          disabled={isConfirming || !isLandPrepDateValid() || isPredictionApplied()}
                          data-testid="confirm-application-button"
                        >
                          {t('confirmApplication')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t('confirmApplicationTitle')}</DialogTitle>
                          <DialogDescription>
                            {t('confirmApplicationMessage')}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="flex flex-col w-full sm:w-[338px] items-start gap-2 relative">
                          <label className="self-stretch font-regular-body-2 text-[#14151c] text-[length:var(--regular-body-2-font-size)] tracking-[var(--regular-body-2-letter-spacing)] leading-[var(--regular-body-2-line-height)]">
                            {t('applicationDate')}
                          </label>
                          <div className="relative w-full z-[100]">
                            <DatePicker
                              selected={selectedDate}
                              onChange={(date: Date | null) => {
                                if (date) {
                                  setSelectedDate(date);
                                  setIsDatePickerOpen(false);
                                } else {
                                  setSelectedDate(undefined);
                                }
                              }}
                              minDate={minDate}
                              maxDate={maxDate}
                              showMonthDropdown
                              showYearDropdown
                              dropdownMode="select"
                              dateFormat="dd/MM/yyyy"
                              placeholderText="dd/mm/yyyy"
                              isClearable
                              open={isDatePickerOpen}
                              onInputClick={() => setIsDatePickerOpen(true)}
                              onClickOutside={() => setIsDatePickerOpen(false)}
                              customInput={
                                <ReadOnlyInput
                                  className="h-[50px] min-w-full border border-[#c2c7d0] shadow-[0px_0px_4px_#0000001a] rounded-md px-3 cursor-pointer"
                                  style={{ caretColor: 'transparent' }}
                                />
                              }
                            />
                            <CalendarIcon className="absolute right-6 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            variant="outline"
                            onClick={() => setIsConfirmDialogOpen(false)}
                            disabled={isConfirming}
                            data-testid="cancel-confirmation-button"
                          >
                            {t('no')}
                          </Button>
                          <Button
                            onClick={handleConfirmApplication}
                            disabled={isConfirming || !selectedDate}
                            className="bg-primary-new500 text-white"
                            data-testid="confirm-button"
                          >
                            {isConfirming ? t('loading') : t('yes')}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardContent>
              </Card>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};